import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { money } from "../lib/helpers";
import { MAS_MAILIN } from "../lib/constants";

export default function PackingSlip() {
  const { id } = useParams();
  const [sub, setSub] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/submissions/${id}`).then((r) => setSub(r.data)).catch(() => setErr(true)); }, [id]);

  const cardDesc = (c) => {
    const yr = c.year && c.year !== "Unknown" ? c.year : "";
    return [yr, c.brand, c.subject].filter(Boolean).join(" ") || "—";
  };

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">Order not found.</div>;
  if (!sub) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><Loader2 className="mx-auto animate-spin text-[#0A0A0A]" size={32} /></div>;

  const short = sub.id.slice(-8);
  const receiveUrl = `${window.location.origin}/admin/receive/${sub.id}`;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-8 py-10">
      <div className="flex justify-between items-center mb-6 no-print">
        <Link to="/portal" className="inline-flex items-center gap-2 text-sm font-bold text-[#0A0A0A] hover:underline"><ArrowLeft size={16} /> Back to Portal</Link>
        <button data-testid="print-slip-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors">
          <Printer size={16} /> Print Packing Slip
        </button>
      </div>

      <div className="print-area bg-white border border-slate-200 rounded-2xl p-8 md:p-10" data-testid="packing-slip">
        <div className="flex justify-between items-start border-b border-slate-200 pb-6">
          <img src="/mas-logo-black.png" alt="MAS Grading" className="h-12 w-auto" />
          <div className="flex items-start gap-5">
            <div className="text-right">
              <div className="font-display font-extrabold text-2xl tracking-tight text-[#0A0A0A]">Packing Slip</div>
              <div className="text-sm text-slate-500 mt-1">Order #{short}</div>
              <div className="text-xs text-slate-400">{new Date(sub.created_at).toLocaleDateString()}</div>
            </div>
            <div className="text-center shrink-0" data-testid="packing-slip-qr">
              <div className="p-1.5 bg-white border border-slate-200 rounded-lg">
                <QRCodeSVG value={receiveUrl} size={72} level="M" />
              </div>
              <div className="text-[8px] uppercase tracking-widest text-slate-400 mt-1">Staff scan</div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Ship your cards to</div>
            <div className="text-[#0A0A0A] font-semibold leading-relaxed">
              {MAS_MAILIN.line1}<br />{MAS_MAILIN.line2}<br />{MAS_MAILIN.line3}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Return to</div>
            <div className="text-[#0A0A0A] leading-relaxed">
              <span className="font-semibold">{sub.return_name}</span><br />
              {sub.return_address}<br />
              <span className="text-slate-500">{sub.email || sub.user_email}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-1 text-sm">
          <span><span className="text-slate-400">Service:</span> <strong className="text-[#0A0A0A]">{sub.tier_name}</strong></span>
          <span><span className="text-slate-400">Cards:</span> <strong className="text-[#0A0A0A]">{sub.card_count}</strong></span>
          <span><span className="text-slate-400">Total paid:</span> <strong className="text-[#0A0A0A]">{money(sub.total_amount)}</strong></span>
          <span><span className="text-slate-400">Insured return value:</span> <strong className="text-[#0A0A0A]">{money(Math.round((sub.declared_total || 0) * 100))}</strong></span>
          <span><span className="text-slate-400">Signature:</span> <strong className="text-[#0A0A0A]">{sub.signature_required ? "Required" : "Not required"}</strong></span>
        </div>

        <table className="w-full mt-6 text-sm border-collapse">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
              <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Card</th><th className="py-2 pr-2">Type</th>
              <th className="py-2 pr-2">Card #</th><th className="py-2 pr-2">Value</th><th className="py-2">Add-ons</th>
            </tr>
          </thead>
          <tbody>
            {sub.cards.map((c, i) => {
              const addons = [c.autograph_auth && "Auto Auth", c.autograph_grade && "Auto Grade"].filter(Boolean).join(", ") || "—";
              return (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-2.5 pr-2 text-slate-400">{i + 1}</td>
                  <td className="py-2.5 pr-2 font-semibold text-[#0A0A0A]">{cardDesc(c)}</td>
                  <td className="py-2.5 pr-2 text-slate-600">{[c.category, c.variant].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="py-2.5 pr-2 text-slate-600">{c.cardnum || "—"}</td>
                  <td className="py-2.5 pr-2 text-slate-600">{c.declared_value ? money(Math.round(Number(c.declared_value) * 100)) : "—"}</td>
                  <td className="py-2.5 text-slate-600">{addons}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-8 rounded-xl bg-[#FAFAFA] border border-slate-200 p-4 text-sm text-slate-600">
          <strong className="text-[#0A0A0A]">Instructions:</strong> Place this slip inside your package with your cards. Pack each card securely (penny sleeve + toploader or semi-rigid). Mail to the address above. Track your submission anytime in your portal.
        </div>
        <p className="text-center text-[10px] text-slate-400 mt-6 tracking-widest uppercase">MAS Grading · grading.mascertified.com · Order #{short}</p>
      </div>
    </div>
  );
}
